import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ApiKeyScope, Me } from "@releo/shared";
import { API_KEY_SCOPES } from "@releo/shared/constants";
import { ConfirmDialog, CopyButton, Mono, toastError } from "@/components/common";
import { Badge, Button, Card, CardHeader, Dialog, EmptyState, Field, Input, PageHeader, Select, Spinner, Table, Td, Th } from "@/components/ui";
import { authPost, http } from "@/lib/api";
import { keys, useApiKeys, useApps, useAuthStatus, useMe } from "@/lib/queries";
import { isOrgAdmin } from "@/lib/roles";
import { formatDate } from "@/lib/utils";

function useProfileForm(onSaved?: (form: HTMLFormElement) => void) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const submit = (toBody: (data: Record<string, string>) => object) => async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setBusy(true);
    try {
      const user = await http.patch<Me>("/me", toBody(Object.fromEntries(new FormData(form)) as Record<string, string>));
      qc.setQueryData(keys.me, user);
      toast.success(t("common.saved"));
      onSaved?.(form);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return { busy, submit };
}

function UnverifiedEmail({ email }: { email: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await authPost("/send-verification-email", { email, callbackURL: "/settings" });
      setSent(true);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-5 mt-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
      <span className="flex-1">{sent ? t("auth.verificationResent") : t("settings.emailUnverified")}</span>
      {!sent && (
        <Button size="sm" variant="outline" loading={busy} onClick={() => void resend()}>
          {t("auth.resendVerification")}
        </Button>
      )}
    </div>
  );
}

function ProfileCard({ me }: { me: Me }) {
  const { t } = useTranslation();
  const { busy, submit } = useProfileForm();
  const status = useAuthStatus();
  return (
    <Card>
      <CardHeader title={t("settings.profile")} description={me.email} />
      {!me.emailVerified && status.data?.emailEnabled && <UnverifiedEmail email={me.email} />}
      <form onSubmit={submit((data) => ({ name: data.name }))} className="flex flex-wrap items-end gap-4 p-5">
        <Field label={t("common.name")} help={me.ssoManaged ? t("settings.nameFromIdp") : undefined} htmlFor="p-name" className="min-w-64 flex-1">
          <Input id="p-name" name="name" defaultValue={me.name} required readOnly={me.ssoManaged} disabled={me.ssoManaged} />
        </Field>
        {!me.ssoManaged && (
          <Button type="submit" loading={busy}>
            {t("common.save")}
          </Button>
        )}
      </form>
    </Card>
  );
}

function PasswordCard({ me }: { me: Me }) {
  const { t } = useTranslation();
  const { busy, submit } = useProfileForm((form) => form.reset());
  if (!me.hasPassword || me.ssoRequired) {
    return (
      <Card>
        <CardHeader title={t("settings.password")} description={t("settings.passwordSso")} />
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader title={t("settings.password")} />
      <form onSubmit={submit((data) => ({ currentPassword: data.currentPassword, newPassword: data.newPassword }))} className="grid gap-4 p-5 md:grid-cols-2">
        <Field label={t("settings.currentPassword")} htmlFor="p-cur">
          <Input id="p-cur" name="currentPassword" type="password" required autoComplete="current-password" />
        </Field>
        <Field label={t("settings.newPassword")} help={t("auth.passwordHint")} htmlFor="p-new">
          <Input id="p-new" name="newPassword" type="password" minLength={10} required autoComplete="new-password" />
        </Field>
        <div className="flex justify-end md:col-span-2">
          <Button type="submit" loading={busy}>
            {t("settings.changePassword")}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function NewKeyDialog({ open, onOpenChange, isAdmin }: { open: boolean; onOpenChange: (o: boolean) => void; isAdmin: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const apps = useApps();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>(["apps:read", "builds:read", "builds:write"]);
  const [appId, setAppId] = useState("");
  const [expires, setExpires] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = (o: boolean) => {
    onOpenChange(o);
    if (!o) {
      setToken(null);
      setName("");
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      const res = await http.post<{ token: string }>("/me/api-keys", {
        name,
        scopes,
        appIds: appId ? [appId] : null,
        expiresAt: expires ? new Date(expires).toISOString() : null,
      });
      setToken(res.token);
      void qc.invalidateQueries({ queryKey: keys.apiKeys });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const curl = token
    ? `curl -X POST ${location.origin}/api/v1/builds \\\n  -H "Authorization: Bearer ${token}" \\\n  -F commit="$GIT_SHA" \\\n  -F file=@app.ipa`
    : "";

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("settings.newKey")}
      wide={!!token}
      footer={
        token ? (
          <Button onClick={() => close(false)}>{t("common.close")}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={create} loading={busy} disabled={!name.trim() || scopes.length === 0}>
              {t("common.create")}
            </Button>
          </>
        )
      }
    >
      {token ? (
        <div className="space-y-4">
          <p className="text-sm">{t("settings.tokenCreated")}</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-xs">{token}</code>
            <CopyButton value={token} />
          </div>
          <div>
            <p className="mb-1.5 text-sm font-medium">{t("settings.ciExample")}</p>
            <pre className="overflow-x-auto rounded-md bg-slate-950 p-3 text-xs text-slate-200">{curl}</pre>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label={t("common.name")} htmlFor="k-name">
            <Input id="k-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="GitHub Actions" autoFocus />
          </Field>
          <fieldset className="space-y-2">
            <legend className="mb-1.5 text-sm font-medium">{t("settings.scopes")}</legend>
            {API_KEY_SCOPES.filter((s) => s !== "admin" || isAdmin).map((scope) => (
              <label key={scope} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-600"
                  checked={scopes.includes(scope)}
                  onChange={(e) => setScopes((s) => (e.target.checked ? [...s, scope] : s.filter((x) => x !== scope)))}
                />
                <Mono>{scope}</Mono>
                <span className="text-fg-muted">{t(`scopes.${scope}`)}</span>
              </label>
            ))}
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("settings.restrictApps")} htmlFor="k-app">
              <Select id="k-app" value={appId} onChange={(e) => setAppId(e.target.value)}>
                <option value="">{t("settings.allApps")}</option>
                {apps.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("settings.expiresAt")} htmlFor="k-exp">
              <Input id="k-exp" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
            </Field>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function ApiKeysCard({ isAdmin }: { isAdmin: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const apiKeys = useApiKeys();
  const apps = useApps();
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader
        title={t("settings.apiKeys")}
        description={t("settings.apiKeysHelp")}
        actions={
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            {t("settings.newKey")}
          </Button>
        }
      />
      {apiKeys.isLoading ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : !apiKeys.data?.length ? (
        <div className="p-5">
          <EmptyState icon={<KeyRound className="h-6 w-6" />} title={t("settings.newKey")} />
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t("common.name")}</Th>
              <Th>{t("settings.scopes")}</Th>
              <Th className="hidden md:table-cell">{t("settings.lastUsed")}</Th>
              <Th className="hidden md:table-cell">{t("settings.expiresAt")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {apiKeys.data.map((k) => (
              <tr key={k.id}>
                <Td>
                  <p className="font-medium">{k.name}</p>
                  <p className="font-mono text-xs text-fg-muted">{k.prefix}…</p>
                  {k.appIds && <p className="text-xs text-fg-muted">{k.appIds.map((id) => apps.data?.find((a) => a.id === id)?.name ?? id).join(", ")}</p>}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {k.scopes.map((s) => (
                      <Badge key={s}>{s}</Badge>
                    ))}
                  </div>
                </Td>
                <Td className="hidden text-fg-muted md:table-cell">{formatDate(k.lastUsedAt, i18n.language)}</Td>
                <Td className="hidden text-fg-muted md:table-cell">{k.expiresAt ? formatDate(k.expiresAt, i18n.language) : t("settings.never")}</Td>
                <Td className="text-right">
                  <ConfirmDialog
                    danger
                    title={`${t("settings.revoke")} « ${k.name} »`}
                    description={t("common.irreversible")}
                    confirmLabel={t("settings.revoke")}
                    onConfirm={async () => {
                      await http.del(`/me/api-keys/${k.id}`);
                      void qc.invalidateQueries({ queryKey: keys.apiKeys });
                    }}
                    trigger={(o) => (
                      <Button variant="ghost" size="sm" onClick={o}>
                        {t("settings.revoke")}
                      </Button>
                    )}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <NewKeyDialog open={open} onOpenChange={setOpen} isAdmin={isAdmin} />
    </Card>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const me = useMe();
  if (!me.data) return <Spinner />;
  return (
    <>
      <PageHeader title={t("settings.title")} />
      <div className="space-y-6">
        <ProfileCard me={me.data} />
        <PasswordCard me={me.data} />
        <ApiKeysCard isAdmin={isOrgAdmin(me.data)} />
      </div>
    </>
  );
}
